<script>
  import { onMount, onDestroy } from 'svelte';
  import { navigate as goto } from 'svelte-routing';
  import { page } from '$stores/pageStore';
  import PopupForm from './persons/PersonForm.svelte';
  import Pagination from '$lib/Pagination.svelte';
  import StudyDetails from '$lib/StudyDetails.svelte';
  import FeatureInfo from '$lib/FeatureInfo.svelte';
  import ConfirmationModal from '$lib/ConfirmationModal.svelte';
  import AuditLogModal from '$lib/AuditLogModal.svelte';
  import { selectedStudy, selectedSite, selectedParticipant } from '$stores/selectionStore';
  import { studyProperties, isFeatureEnabled, fetchStudyProperties } from '$stores/configStore';
  import StudyHeader from '$lib/StudyHeader.svelte';
  import Toolbar from '$lib/Toolbar.svelte';
  import Footer from '../lib/Footer.svelte';
  import { FontAwesomeIcon } from '@fortawesome/svelte-fontawesome';
  import {
    faChevronLeft,
    faChevronRight,
    faSquarePlus,
    faTrashCan,
    faPencil,
    faRotate,
    faSearch,
    faCircleXmark,
    faHistory,
    faPlus,
    faCopy,
    faEllipsis,
    faChevronDown
  } from '@fortawesome/pro-light-svg-icons';
  import { formatToLocalDateTime, isWithinLastHours, isOlderThanHours } from '$utils/dateUtils.js';
  import { user, userRole, userRoles, userReadOnly } from '$stores/sessionStore';
  import { hasAuditLogAccess, hasAccessUiAction } from '$utils/permissionUtils.js';
  import { exportToExcel, generateExportFilename } from '$utils/export.js';
  const browser = typeof window !== 'undefined';
  // TODO: replace SvelteKit `error` — throw a plain Error or surface via a store
  import PatientsInfoCards from '$lib/PatientsInfoCards.svelte';

  /** @type {import('./$types').PageData} */
  // export let data;

  // Use local API endpoints instead of direct API with key
  const API_BASE_URL = '/api/proxy';

  let persons = [];
  let allPersons = [];
  let resultCount = 0;
  let currentPage = 1;
  let totalPages = 1;
  let limit = 20;
  let isLoading = true;
  let searchText = '';
  let isExporting = false;
  let hasError = false;
  let errorMessage = '';
  let selectedType = 'ALL';
  /** When true, only list participants with a non-empty Subject.properties.syncProvider */
  let filterHasSyncProvider = false;

  let show = false;
  let showForm = false;
  let showConfirmation = false;
  let showAuditLog = false;
  let editPerson = null;
  let personToDelete = null;
  let auditLogPerson = null;
  let studyId = null;
  let currentFetch = null;
  let isInitialized = false;

  let showPersonMenu = false;
  let selectedPersonForMenu = null;

  let syncProviderMenuPersonId = null;
  let showRemoveSyncProviderConfirmation = false;
  let personForSyncProviderRemoval = null;

  // Feature flag for participants card view
  let isParticipantsCardViewEnabled = true;

  // Reactive variable for feature flags
  $: {
    isParticipantsCardViewEnabled = isFeatureEnabled('cardViews.participants', $studyProperties);
  }

  $: isUpgradeEligibilityEnabled = isFeatureEnabled('upgradeEligibility', $studyProperties);
  $: canToggleEligibility = hasAccessUiAction(
    'participants.allPersons.toggleEligibility',
    $userRoles,
    $studyProperties,
    $userReadOnly
  );

  // Check if syncProvider property is configured in study properties
  $: syncProviderConfig = $studyProperties?.properties?.find(p => p.key === 'syncProvider' && p.active !== false);
  $: showSyncProviderColumn = !!syncProviderConfig;

  // Helper function to get the label for a syncProvider code
  function getSyncProviderLabel(code) {
    if (!code || !syncProviderConfig?.options) return code || '';
    const option = syncProviderConfig.options.find(opt => opt.code === code);
    return option?.label || code;
  }

  function hasSyncProviderValue(person) {
    const v = person?.properties?.syncProvider;
    return v != null && v !== '';
  }

  function syncProviderFilterQuery() {
    return filterHasSyncProvider && showSyncProviderColumn ? '&hasSyncProvider=true' : '';
  }

  // Subscribe to selectedStudy changes to fetch properties
  $: if ($selectedStudy?._id) {
    fetchStudyProperties($selectedStudy._id);
  } else {
    studyProperties.set(null);
  }

  // Add cleanup on component destroy
  onDestroy(() => {
    if (currentFetch) {
      currentFetch.abort();
    }
  });

  // Add a store subscription, but only in the browser
  $: if (browser && isInitialized && $selectedStudy) {
    //console.log('Store subscription triggered, selectedStudy changed:', $selectedStudy._id);
    fetchPersons(currentPage).catch(err => {
      console.error('Error in store subscription:', err);
      hasError = true;
      errorMessage = err.message;
    });
    fetchAllPersons().catch(err => {
      console.error('Error fetching all persons:', err);
    });
  }

  onMount(async () => {
    if (!browser) return;  // Don't run on server

    try {
      //console.log('Page mounting, selectedStudy:', $selectedStudy);
      
      selectedParticipant.set(null);

      // Get search text from URL parameter if it exists
      const urlSearchText = $page.url.searchParams.get('search');
      if (urlSearchText) {
        searchText = urlSearchText;
      }

      // Get page number from URL parameter if it exists
      const urlPage = parseInt($page.url.searchParams.get('page')) || 1;
      currentPage = urlPage;

      if (!$selectedStudy) {
        //console.log('No study selected, redirecting to study selection');
        goto('/studies');
        return;
      }

      // Mark as initialized after initial setup
      isInitialized = true;
    } catch (err) {
      console.error('Error in onMount:', err);
      hasError = true;
      errorMessage = err.message;
    }

    document.addEventListener('click', handleClickOutside);
    return () => {
      document.removeEventListener('click', handleClickOutside);
    };
  });

  // Create an abort controller factory
  function createAbortController() {
    if (currentFetch) {
      currentFetch.abort();
    }
    return new AbortController();
  }

  async function fetchPersons(page = 1) {
    const requestId = Date.now();  // Add unique ID for this request
    //console.log(`Starting fetch request ${requestId}`);

    // Create a new abort controller for this request
    const abortController = createAbortController();
    currentFetch = abortController;

    if (!$selectedStudy || !$selectedStudy._id) {
        console.warn(`[Request ${requestId}] No study selected or invalid study data`);
        persons = [];
        resultCount = 0;
        currentPage = 1;
        totalPages = 1;
        return;
    }

    if (!API_BASE_URL) {
        console.error(`[Request ${requestId}] Missing API configuration`);
        return;
    }

    try {
        isLoading = true;
        let url = `${API_BASE_URL}/persons?page=${page}&pageSize=${limit}&searchText=${searchText}&sortOrder=asc&studyGUID=${$selectedStudy._id}${syncProviderFilterQuery()}`;
        
        // Add status filter if not ALL
        if (selectedType !== 'ALL') {
          url += `&subjectStatus=${selectedType}`;
        }
        
        const res = await fetch(url, {
            signal: abortController.signal
        });

        // Check if this is still the current fetch
        if (currentFetch !== abortController) {
            console.log(`[Request ${requestId}] Fetch was superseded by newer request`);
            return;
        }

        if (!res.ok) {
            const errorData = await res.json().catch(() => ({}));
            console.error(`[Request ${requestId}] API Error Response:`, {
                status: res.status,
                statusText: res.statusText,
                errorData
            });
            throw new Error(`Failed to fetch persons: ${res.status} ${errorData.message || ''}`);
        }

        const responseData = await res.json();

        // Only update state if this is still the current fetch
        if (currentFetch === abortController) {
            resultCount = responseData.totalCount || 0;
            currentPage = responseData.page || 1;
            totalPages = responseData.totalPages || 1;
            persons = responseData.data;
        }

    } catch (err) {
        if (err.name === 'AbortError') {
            console.log(`[Request ${requestId}] Fetch was aborted`);
            return;
        }
        console.error(`[Request ${requestId}] Error fetching persons:`, err);
        persons = [];
        resultCount = 0;
        currentPage = 1;
        totalPages = 1;
    } finally {
        if (currentFetch === abortController) {
            isLoading = false;
            currentFetch = null;
        }
    }
  }

  async function handleCreatePerson(event) {
    const newPerson = event.detail;
    try {
      const res = await fetch(`${API_BASE_URL}/persons`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json'
        },
        body: JSON.stringify(newPerson),
      });

      if (!res.ok) {
        throw new Error('Failed to create person');
      }

      // Get the newly created person data
      const response = await res.json();
      console.log('Person creation response:', response);
      
      // Extract the ID from the nested response structure
      const personId = response.data?.insertedId;
      
      if (!personId) {
        console.error('Created person response is missing ID:', response);
        throw new Error('Invalid person data received from server');
      }
      
      console.log('Created person with ID:', personId);
      
      // Fetch the complete person data since the creation response only has the ID
      try {
        const personRes = await fetch(`${API_BASE_URL}/persons/${personId}`);
        
        if (!personRes.ok) {
          throw new Error('Failed to fetch created person data');
        }
        
        const fullPersonData = await personRes.json();
        console.log('Fetched full person data:', fullPersonData);
        
        // Set the newly created person as the selected participant
        selectedParticipant.set(fullPersonData);
        
        // Generate schedule for the newly created person
        if ($selectedStudy) {
          try {
            const startDate = new Date(); // Use current date as start date
            
            const scheduleRes = await fetch(`${API_BASE_URL}/subjectSchedule/generate-subject-schedule`, {
              method: 'POST',
              headers: {
                'Content-Type': 'application/json'
              },
              body: JSON.stringify({
                subjectGUID: personId,
                studyGUID: $selectedStudy._id,
                startDate: startDate.toISOString()
              })
            });
            
            if (!scheduleRes.ok) {
              const errorData = await scheduleRes.json().catch(() => ({}));
              console.error('Failed to generate schedule for new participant:', errorData.message || 'Unknown error');
            } else {
              const scheduleData = await scheduleRes.json();
              console.log('Schedule generated successfully:', scheduleData);
            }
          } catch (scheduleErr) {
            console.error('Error generating schedule:', scheduleErr);
          }
        }

        // Navigate to the person detail page
        goto(`/schedule?id=${personId}`);
      } catch (fetchErr) {
        console.error('Error fetching complete person data:', fetchErr);
      }
      
      // We don't need to fetch persons or close the form since we're navigating away
    } catch (err) {
      console.error('Error creating person:', err);
      await fetchPersons();
      showForm = false;
    }
  }

  async function handleEditPerson(event) {
    const updatedPerson = event.detail;
    try {
      const res = await fetch(`${API_BASE_URL}/persons/${updatedPerson._id}`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json'
        },
        body: JSON.stringify(updatedPerson),
      });

      if (!res.ok) {
        const errorData = await res.json().catch(() => ({}));
        throw new Error(errorData.message || 'Failed to update person');
      }

      await fetchPersons();
      await fetchAllPersons();
      showForm = false;
      editPerson = null;
    } catch (err) {
      console.error('Error updating person:', err);
    }
  }

  async function confirmDelete() {
    if (personToDelete) {
      try {
        const res = await fetch(`${API_BASE_URL}/persons/${personToDelete._id}`, {
          method: 'DELETE',
          headers: {
            'Content-Type': 'application/json'
          },
        });

        if (!res.ok) {
          throw new Error('Failed to delete person');
        }

        const data = await res.json();

        await fetchPersons();
        await fetchAllPersons();
        showConfirmation = false;
        personToDelete = null;
      } catch (err) {
        console.error('Error deleting person:', err);
      }
    }
  }

  function handleDeleteClick(person) {
    personToDelete = person;
    showConfirmation = true;
  }

  function handleAuditLogClick(person) {
    auditLogPerson = person;
    showAuditLog = true;
  }

  function handleEditClick(person) {
    editPerson = person;
    showForm = true;
  }

  function handlePageChange(newPage) {
    if (newPage > 0 && newPage <= totalPages) {
      // Update URL with page number
      const url = new URL(window.location);
      url.searchParams.set('page', newPage.toString());
      goto(url.toString(), { replaceState: true });

      fetchPersons(newPage);
    }
  }

  function handleNewClick() {
    studyId = $selectedStudy ? $selectedStudy.studyId : 'NONE';
    editPerson = null;
    showForm = true;
  }

  function handlePersonClick(person) {
    // Set the selectedSite first, then selectedParticipant
    if (person.siteGUID) {
      // Find the site from the site details we already have
      const site = {
        _id: person.siteGUID,
        siteId: person.siteId,
        siteName: person.siteName,
        country: person.country
      };
      selectedSite.set(site);
    }
    selectedParticipant.set(person);
    goto(`/person`);
  }

  function handleRefresh() {
    fetchPersons(1);
    fetchAllPersons();
  }

  function handleSearch() {
    // Update URL with search text and reset page to 1
    const url = new URL(window.location);
    if (searchText) {
      url.searchParams.set('search', searchText);
    } else {
      url.searchParams.delete('search');
    }
    url.searchParams.set('page', '1'); // Reset to page 1 when searching
    goto(url.toString(), { replaceState: true });
    fetchPersons(1);
    fetchAllPersons();
  }

  function handleClearSearch() {
    searchText = '';
    // Reset page to 1 when clearing search
    currentPage = 1;
    handleSearch();
  }

  // Add computed property for edit permissions using the centralized hasRole function
  $: canAddPersons = hasAccessUiAction(
    'participants.allPersons.add',
    $userRoles,
    $studyProperties,
    $userReadOnly
  );
  $: canEditPersons = hasAccessUiAction(
    'participants.allPersons.edit',
    $userRoles,
    $studyProperties,
    $userReadOnly
  );
  $: canDeletePersons = hasAccessUiAction(
    'participants.allPersons.delete',
    $userRoles,
    $studyProperties,
    $userReadOnly
  );
  $: canViewAuditLogs = hasAuditLogAccess($userRoles, $studyProperties, $userReadOnly);

  // KPI-card loading flag. True while the bulk /persons fetch is in flight,
  // false on both success and error so the cards always end up showing real
  // numbers (or 0 if genuinely empty) rather than a misleading "0" during
  // boot. See PatientsInfoCards.svelte for the rendering side.
  let isLoadingKpi = true;

  async function fetchAllPersons() {
    if (!$selectedStudy || !$selectedStudy._id) {
      allPersons = [];
      isLoadingKpi = false;
      return;
    }

    isLoadingKpi = true;
    try {
      let url = `${API_BASE_URL}/persons?page=1&pageSize=999999&searchText=${searchText}&sortOrder=asc&studyGUID=${$selectedStudy._id}${syncProviderFilterQuery()}`;

      // Add status filter if not ALL
      if (selectedType !== 'ALL') {
        url += `&subjectStatus=${selectedType}`;
      }

      const res = await fetch(url);

      if (!res.ok) {
        throw new Error('Failed to fetch all persons');
      }

      const responseData = await res.json();
      allPersons = responseData.data || [];
    } catch (err) {
      console.error('Error fetching all persons:', err);
      allPersons = [];
    } finally {
      isLoadingKpi = false;
    }
  }

  async function handleExportExcel() {
    try {
      isExporting = true;
      
      // Fetch all persons data directly for export
      if (!$selectedStudy || !$selectedStudy._id) {
        throw new Error('No study selected');
      }

      let url = `${API_BASE_URL}/persons?page=1&pageSize=999999&searchText=${searchText}&sortOrder=asc&studyGUID=${$selectedStudy._id}${syncProviderFilterQuery()}`;
      
      // Add status filter if not ALL
      if (selectedType !== 'ALL') {
        url += `&subjectStatus=${selectedType}`;
      }
      
      const res = await fetch(url);

      if (!res.ok) {
        throw new Error('Failed to fetch persons data for export');
      }

      const responseData = await res.json();
      const allPersons = responseData.data || [];

      const now = new Date();
      const dateStr = now
        .toLocaleString('en-US', {
          day: '2-digit',
          month: 'short',
          year: 'numeric',
          hour: '2-digit',
          minute: '2-digit',
          hour12: false,
        })
        .replace(/,/g, '')
        .replace(/\s+/g, '')
        .replace(/:/g, '')
        .replace(/(\d{2})([A-Za-z]{3})(\d{4})_(\d{2})(\d{2})/, '$2$1$3_$4$5');

      const filename = generateExportFilename({
        protocolNumber: $selectedStudy?.protocolNumber,
        reportType: 'AllParticipants'
      });

      const formattedData = allPersons.map((person) => {
        const baseData = {
          Protocol: $selectedStudy?.protocolNumber || '',
          'Site ID': person.siteId || '',
          'Site Name': person.siteName || '',
          Country: person.country || '',
          Participant: person.subjectId || '',
          Device: person.properties?.enabledDevice || 'Any',
          'First Reading (Local)': person.firstReadingDate
            ? formatToLocalDateTime(person.firstReadingDate, person.timezoneCode)
            : '',
          'Latest Reading (Local)': person.latestReadingDate
            ? formatToLocalDateTime(person.latestReadingDate, person.timezoneCode)
            : '',
          Language: person.language ? `${person.flag} ${person.language}` : '',
          Timezone: person.timezoneCode || '',
          Blinded: person.blinded ? 'Y' : 'N',
          Status: person.statusLabel || '',
          'Device Manufacturer': person.glucoseManufacturer || '',
          'Device Model': person.glucoseModel || '',
        };
        
        // Add syncProvider if configured
        if (showSyncProviderColumn) {
          baseData[syncProviderConfig.label || 'Sync Provider'] = getSyncProviderLabel(person.properties?.syncProvider);
        }
        
        return baseData;
      });

      await exportToExcel(formattedData, filename);
    } catch (error) {
      console.error('Error exporting to Excel:', error);
    } finally {
      isExporting = false;
    }
  }

  function handlePersonMenuClick(event, person) {
    event.stopPropagation();
    selectedPersonForMenu = person;
    showPersonMenu = !showPersonMenu;
  }

  function handlePersonMenuClose() {
    showPersonMenu = false;
    selectedPersonForMenu = null;
  }

  function toggleSyncProviderMenu(person) {
    const id = String(person._id);
    syncProviderMenuPersonId = syncProviderMenuPersonId === id ? null : id;
  }

  function openRemoveSyncProviderConfirm(person) {
    syncProviderMenuPersonId = null;
    personForSyncProviderRemoval = person;
    showRemoveSyncProviderConfirmation = true;
  }

  async function confirmRemoveSyncProvider() {
    const p = personForSyncProviderRemoval;
    if (!p) return;
    try {
      const res = await fetch(`${API_BASE_URL}/persons/${p._id}/properties/syncProvider`, {
        method: 'DELETE'
      });
      const body = await res.json().catch(() => ({}));
      if (!res.ok) {
        throw new Error(body.message || body.error || 'Failed to remove sync provider');
      }
      await fetchPersons(currentPage);
      await fetchAllPersons();
    } catch (err) {
      console.error('Error removing sync provider:', err);
      alert(err.message || 'Failed to remove sync provider');
    }
  }

  // Add click handler to close menu when clicking outside
  function handleClickOutside(event) {
    if (showPersonMenu && !event.target.closest('.menu-container')) {
      handlePersonMenuClose();
    }
    if (syncProviderMenuPersonId && !event.target.closest('.sync-provider-menu-container')) {
      syncProviderMenuPersonId = null;
    }
  }
</script>

<PopupForm
  {showForm}
  {editPerson}
  on:submit={editPerson ? handleEditPerson : handleCreatePerson}
  on:close={() => (showForm = false)}
/>

<ConfirmationModal
  {showConfirmation}
  message="Are you sure you want to delete this participant?"
  on:confirm={confirmDelete}
  on:close={() => (showConfirmation = false)}
/>

<ConfirmationModal
  showConfirmation={showRemoveSyncProviderConfirmation}
  message={personForSyncProviderRemoval
    ? `Remove the sync provider property from participant ${personForSyncProviderRemoval.subjectId || ''}? The field will be deleted from Subject.properties (not cleared to empty).`
    : ''}
  confirmPhrase="remove"
  on:confirm={confirmRemoveSyncProvider}
  on:close={() => {
    showRemoveSyncProviderConfirmation = false;
    personForSyncProviderRemoval = null;
  }}
/>

<AuditLogModal
  showModal={showAuditLog}
  documentId={auditLogPerson?._id}
  collection="Subject"
  title={`Audit Logs - ${auditLogPerson?.firstName || ''} ${auditLogPerson?.lastName || 'Participant'}`}
  on:close={() => (showAuditLog = false)}
/>

{#if hasError}
  <div class="p-4 bg-red-100 dark:bg-red-900 text-red-700 dark:text-red-100 rounded-lg mb-4">
    <p>An error occurred: {errorMessage}</p>
    <button 
      class="mt-2 px-4 py-2 bg-red-200 dark:bg-red-800 rounded hover:bg-red-300 dark:hover:bg-red-700"
      on:click={() => {
        hasError = false;
        errorMessage = '';
        if ($selectedStudy) {
          fetchPersons(currentPage);
        }
      }}
    >
      Try Again
    </button>
  </div>
{/if}

<StudyHeader />

<FeatureInfo 
  title="All Participants Management" 
  description="This page allows you to view and manage all participants across all sites for the selected study. You can search, create, edit, 
  and delete participants as well as export participant data to Excel. The header card shows information about the current study."
  marginBottom="mb-0"
/>

{#if $selectedStudy}
  <StudyDetails showOptionsMenu={false} />
{/if}

{#if isParticipantsCardViewEnabled}
  <PatientsInfoCards persons={allPersons} studyProperties={$studyProperties} loading={isLoadingKpi} />
{/if}

<Toolbar title="All Participants" itemCountTitle="Participants" itemCount={resultCount}>
  <div class="flex items-center gap-4">
    <div class="flex items-center gap-2">
      <div class="relative">
        <input
          type="text"
          placeholder="Search"
          bind:value={searchText}
          on:keydown={(e) => e.key === 'Enter' && handleSearch()}
          class="px-3 py-1.5 text-xs text-gray-800 dark:text-gray-100 bg-white dark:bg-gray-700 border border-gray-300 dark:border-gray-600 rounded-full focus:outline-none focus:ring-2 focus:ring-blue-500 dark:focus:ring-blue-400 pr-8"
        />
        {#if searchText}
          <button
            on:click={handleClearSearch}
            class="absolute right-2 top-1/2 -translate-y-1/2 text-gray-500 hover:text-gray-700 dark:text-gray-400 dark:hover:text-gray-200"
            title="Clear search"
          >
            <FontAwesomeIcon icon={faCircleXmark} />
          </button>
        {/if}
      </div>
      <button on:click={handleSearch} title="Search">
        <FontAwesomeIcon icon={faSearch} />
      </button>
    </div>
    <div class="flex items-baseline gap-2">
      Status:
      <select
        bind:value={selectedType}
        on:change={() => { fetchPersons(1); fetchAllPersons(); }}
        class="form-select h-6 border px-2 p-0 rounded text-sm dark:bg-gray-700 dark:border-gray-600 text-gray-800 dark:text-gray-100 bg-white appearance-none"
      >
        <option value="ALL" selected>All</option>
        <option value="ENRO">Active</option>
        <option value="COMPD">End of Participation</option>
      </select>
    </div>
    {#if showSyncProviderColumn}
      <label class="flex items-center gap-2 text-sm text-gray-800 dark:text-gray-200 cursor-pointer select-none">
        <input
          type="checkbox"
          bind:checked={filterHasSyncProvider}
          on:change={() => { fetchPersons(1); fetchAllPersons(); }}
          class="rounded border-gray-300 dark:border-gray-600"
        />
        Has sync provider
      </label>
    {/if}
    <button on:click={handleRefresh} class="pagination mx-1 p-0" title="Refresh">
      <FontAwesomeIcon icon={faRotate} />
    </button>
  </div>
</Toolbar>

<div class="px-1 pt-0">
  {#if persons.length > 0}
    <table class="min-w-full border-collapse table-auto">
      <thead>
        <tr class="text-sm text-black dark:text-slate-300 bg-stone-200/50 dark:bg-gray-800/90">
          <th class="px-4 py-2 text-left bold">Site ID</th>
          <th class="px-4 py-2 text-left bold">Country</th>
          <th class="px-4 py-2 text-left bold">Participant</th>
          <th class="px-4 py-2 text-left bold">First Glucose (Local)</th>
          <th class="px-4 py-2 text-left bold">Latest Glucose (Local)</th>
          <th class="px-4 py-2 text-left bold">Active</th>
          {#if showSyncProviderColumn}
            <th class="px-4 py-2 text-left bold">{syncProviderConfig.label || 'Sync Provider'}</th>
          {/if}
          {#if isUpgradeEligibilityEnabled && canToggleEligibility}
            <th class="px-4 py-2 text-left bold">Eligible</th>
          {/if}
          <th class="px-4 py-2 text-left bold">Status</th>
        </tr>
      </thead>
      <tbody>
        {#each persons as person, index}
          <tr
            class="text-sm hover:bg-white dark:hover:bg-gray-775 text-gray-800 dark:text-slate-300 {index %
              2 ===
            0
              ? 'bg-stone-50 dark:bg-gray-725'
              : 'bg-stone-75 dark:bg-gray-750'}"
          >
            <td class="px-4 py-2">{person.siteId || ''}</td
            >
            <td class="px-4 py-2">{person.country || ''}</td
            >
            <td class="px-4 py-2 cursor-pointer" on:click={() => handlePersonClick(person)}
              >{person.subjectId}</td
            >
            <td class="px-4 py-2">
              {#if person.firstReadingDate}
                <div class="flex items-center gap-2">
                  <!-- <div class="w-2 h-2 rounded-full border border-gray-400"></div> -->
                  <div>
                    <div class="text-xs">
                      {formatToLocalDateTime(person.firstReadingDate, person.timezoneCode)}
                    </div>
                    <!-- <div class="text-xs">
                      {person.firstGlucoseManufacturer && person.firstGlucoseModel
                        ? `${person.firstGlucoseManufacturer} ${person.firstGlucoseModel}`
                        : ''}
                    </div> -->
                  </div>
                </div>
              {/if}
            </td>
            <td class="px-4 py-2 cursor-pointer">
              {#if person.latestReadingDate}
                <div class="flex items-center gap-2">
                  <div
                    class={`w-2 h-2 rounded-full border ${
                      isOlderThanHours(person.latestReadingDate, 24)
                        ? 'border-gray-400'
                        : isOlderThanHours(person.latestReadingDate, 3)
                          ? person.glucoseManufacturer === 'Abbott' ||
                            person.glucoseManufacturer === 'Dexcom'
                            ? 'bg-orange-500 border-orange-500'
                            : 'border-blue-500'
                          : person.glucoseManufacturer === 'Abbott' ||
                              person.glucoseManufacturer === 'Dexcom'
                            ? 'bg-green-500 border-green-500'
                            : 'bg-blue-500 border-blue-500'
                    }`}
                    title="Color coding: Green = Recent (<3h, Abbott/Dexcom), Blue = Recent (<3h, other), Orange = Older (3-24h, Abbott/Dexcom), Gray = Outdated (>24h)"
                  ></div>
                  <div>
                    <div class="text-xs">
                      {formatToLocalDateTime(person.latestReadingDate, person.timezoneCode)}
                    </div>
                    <!-- <div class="text-xs">
                      {person.glucoseManufacturer && person.glucoseModel
                        ? `${person.glucoseManufacturer} ${person.glucoseModel}`
                        : ''}
                    </div> -->
                  </div>
                </div>
              {/if}
            </td>
            <td class="px-4 py-2 cursor-pointer" on:click={() => handlePersonClick(person)}>
              {#if person.subjectActivities && person.subjectActivities.length > 0}
                {#each person.subjectActivities.filter(activity => activity.typeCode === 'MEAS' && activity.statusCode === 'START') as activity}
                  <div class="flex items-center gap-2">
                    <span class="text-xs">Yes</span>
                  </div>
                {/each}
                {#if person.subjectActivities.filter(activity => activity.typeCode === 'MEAS' && activity.statusCode === 'START').length === 0}
                  <span class="text-xs text-gray-500 dark:text-gray-400">No</span>
                {/if}
              {:else}
                <span class="text-xs text-gray-500 dark:text-gray-400">No</span>
              {/if}
            </td>
            {#if showSyncProviderColumn}
              <td class="px-4 py-2 align-top">
                {#if hasSyncProviderValue(person)}
                  <div class="sync-provider-menu-container relative inline-flex max-w-full flex-col gap-0.5">
                    <button
                      type="button"
                      class="inline-flex max-w-full items-center gap-1 rounded px-0 py-0.5 text-left text-xs text-gray-800 underline decoration-dotted decoration-gray-400 hover:text-blue-700 dark:text-gray-200 dark:hover:text-blue-300"
                      title="Sync provider actions"
                      on:click|stopPropagation={() => toggleSyncProviderMenu(person)}
                    >
                      <span class="truncate">{getSyncProviderLabel(person.properties?.syncProvider)}</span>
                      <FontAwesomeIcon icon={faChevronDown} class="shrink-0 text-[0.65rem] opacity-70" />
                    </button>
                    {#if syncProviderMenuPersonId === String(person._id)}
                      <!-- svelte-ignore a11y-click-events-have-key-events -->
                      <!-- svelte-ignore a11y-no-static-element-interactions -->
                      <div
                        class="absolute left-0 top-full z-30 mt-1 min-w-[11rem] rounded-md border border-gray-200 bg-white py-1 shadow-lg dark:border-gray-600 dark:bg-gray-800"
                        on:click|stopPropagation
                      >
                        {#if canEditPersons}
                          <button
                            type="button"
                            class="block w-full px-3 py-2 text-left text-xs text-red-600 hover:bg-stone-100 dark:text-red-400 dark:hover:bg-gray-700"
                            on:click|stopPropagation={() => openRemoveSyncProviderConfirm(person)}
                          >
                            Remove sync provider…
                          </button>
                        {:else}
                          <span class="block px-3 py-2 text-xs text-gray-500 dark:text-gray-400">No actions</span>
                        {/if}
                      </div>
                    {/if}
                  </div>
                {:else}
                  <span class="text-xs text-gray-500 dark:text-gray-400">—</span>
                {/if}
              </td>
            {/if}
            {#if isUpgradeEligibilityEnabled && canToggleEligibility}
              <td class="px-4 py-2">
                <span
                  class="inline-flex text-xs px-2 py-0.5 rounded-full ring-1 ring-inset {person.upgradeEligible ? 'text-green-600 ring-green-600' : 'text-gray-500 ring-gray-400'}"
                >
                  {person.upgradeEligible ? 'Yes' : 'No'}
                </span>
              </td>
            {/if}

            <td class="w-px px-4 py-2 cursor-pointer">
              <span
                class="inline-flex items-center rounded-md px-2 py-1 text-xs font-medium ring-1 ring-inset"
                class:text-green-600={person.statusLabel === 'Screened/Enrolled'}
                class:ring-green-600={person.statusLabel === 'Screened/Enrolled'}
                class:text-green-500={person.statusLabel === 'Enrolled'}
                class:ring-green-500={person.statusLabel === 'Enrolled'}
                class:text-gray-600={person.statusLabel === 'Discontinued'}
                class:ring-gray-600={person.statusLabel === 'Discontinued'}
                class:text-gray-500={person.statusLabel === 'Screen Failed'}
                class:ring-gray-500={person.statusLabel === 'Screen Failed'}
                class:text-sky-600={person.statusLabel === 'Completed'}
                class:ring-sky-600={person.statusLabel === 'Completed'}
              >
                {person.statusLabel}
              </span>
            </td>
            
          </tr>
        {/each}
      </tbody>
    </table>
  {:else}
    <div class="text-center py-8 text-gray-500 dark:text-gray-400">
      No participants found
    </div>
  {/if}
</div>

<!-- Add Control Buttons -->
<Footer>
  <Pagination {currentPage} {totalPages} onPageChange={handlePageChange} />
  <div>
    <button
      on:click={handleExportExcel}
      class="bg-stone-300 dark:bg-gray-600 text-black dark:text-white py-1 px-4 rounded-full text-xs"
      >Excel</button
    >
  </div>
</Footer>

<!-- Upgrade eligibility confirmation modal -->
<!-- Add loading overlay for export -->
{#if isExporting}
  <div class="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
    <div class="bg-white dark:bg-gray-800 p-6 rounded-lg shadow-lg flex flex-col items-center">
      <div class="loader mb-4"></div>
      <p class="text-sm text-gray-700 dark:text-gray-300">Preparing Excel export...</p>
    </div>
  </div>
{/if}

<style>
  .loader {
    border: 2px solid #20669c;
    border-radius: 50%;
    border-top: 4px solid #98acb4;
    width: 32px;
    height: 32px;
    animation: spin 2s linear infinite;
  }

  @keyframes spin {
    0% {
      transform: rotate(0deg);
    }
    100% {
      transform: rotate(360deg);
    }
  }

  .fixed {
    position: fixed;
  }

  .inset-0 {
    top: 0;
    right: 0;
    bottom: 0;
    left: 0;
  }

  .bg-opacity-50 {
    --tw-bg-opacity: 0.5;
  }

  .z-50 {
    z-index: 50;
  }

  .shadow-lg {
    --tw-shadow: 0 10px 15px -3px rgb(0 0 0 / 0.1), 0 4px 6px -4px rgb(0 0 0 / 0.1);
    --tw-shadow-colored: 0 10px 15px -3px var(--tw-shadow-color),
      0 4px 6px -4px var(--tw-shadow-color);
    box-shadow: var(--tw-ring-offset-shadow, 0 0 #0000), var(--tw-ring-shadow, 0 0 #0000),
      var(--tw-shadow);
  }
</style> 